<template>
  <div id="pageItemList">
    <page-header></page-header>
    <v-container grid-list-xl fluid>
      <v-layout row wrap>
        <v-flex lg12>
          <v-card>
            <v-toolbar card color="white">
              <v-text-field
                flat
                solo
                prepend-icon="search"
                :placeholder="$t('fulltextSearch')"
                v-model="searchInput"
                hide-details
                class="hidden-sm-and-down"
              ></v-text-field>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="vehicles"
                :rows-per-page-items="[10, 25, 50]"
                :pagination.sync="pagination"
                :total-items="vehiclesCount"
                :loading="vehicleIsLoading"
                class="elevation-1"
                :hide-actions="true"
              >
                <template slot="headerCell" slot-scope="props">
                  <v-tooltip top v-if="props.header.fullText">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        {{ props.header.text }}
                      </span>
                    </template>
                    <span>
                      {{ props.header.fullText }}
                    </span>
                  </v-tooltip>
                  <span v-else>
                    {{ props.header.text }}
                  </span>
                </template>
                <template slot="items" slot-scope="props">
                  <tr @click="props.expanded = !props.expanded">
                    <td><au-vehicle :vehicle="props.item" /></td>
                    <td>{{ props.item.odometer }} km</td>
                    <td>{{ getLastSeen(props.item.last_seen) }}</td>
                    <td v-if="hasDetailsPermission">
                      {{ $t("vehicleStatus." + props.item.status) }}
                    </td>
                    <td v-if="hasDetailsPermission">
                      <v-chip
                        disabled
                        small
                        :color="
                          props.item.faults[0].count > 0
                            ? 'red lighten-1'
                            : 'white'
                        "
                        :text-color="
                          props.item.faults[0].count > 0
                            ? 'black'
                            : 'grey lighten-1'
                        "
                      >
                        {{ props.item.faults[0].count }}
                      </v-chip>
                    </td>
                    <td v-if="hasDetailsPermission">
                      <v-chip
                        disabled
                        small
                        :color="
                          props.item.faults[1].count > 0
                            ? 'orange lighten-1'
                            : 'white'
                        "
                        :text-color="
                          props.item.faults[1].count > 0
                            ? 'black'
                            : 'grey lighten-1'
                        "
                      >
                        {{ props.item.faults[1].count }}
                      </v-chip>
                    </td>
                    <td v-if="hasDetailsPermission">
                      <v-chip
                        disabled
                        small
                        :color="
                          props.item.faults[2].count > 0
                            ? 'yellow lighten-1'
                            : 'white'
                        "
                        :text-color="
                          props.item.faults[2].count > 0
                            ? 'black'
                            : 'grey lighten-1'
                        "
                      >
                        {{ props.item.faults[2].count }}
                      </v-chip>
                    </td>
                    <td v-if="hasDetailsPermission">
                      <v-chip
                        disabled
                        small
                        :color="
                          props.item.faults[3].count > 0
                            ? 'grey lighten-3'
                            : 'white'
                        "
                        :text-color="
                          props.item.faults[3].count > 0
                            ? 'black'
                            : 'grey lighten-1'
                        "
                      >
                        {{ props.item.faults[3].count }}
                      </v-chip>
                    </td>

                    <td
                      v-if="hasManagementPermission || hasSparePartsPermission"
                    >
                      <action-tooltip
                        icon="memory"
                        color="teal darken-3"
                        :tooltip="$t('spareParts')"
                        :to="{
                          name: 'VehicleSpareParts',
                          params: { vehicleId: props.item.id }
                        }"
                        v-if="hasSparePartsPermission"
                      ></action-tooltip>
                      <action-tooltip
                        icon="edit"
                        color="info"
                        :tooltip="$t('edit')"
                        @click="editAction(props.item.id)"
                        v-if="hasManagementPermission"
                      ></action-tooltip>
                      <action-tooltip
                        icon="delete"
                        color="red"
                        :tooltip="$t('delete')"
                        @click="$refs.deleteDialog.deleteAction(props.item)"
                        v-if="hasManagementPermission"
                      ></action-tooltip>
                    </td>
                  </tr>
                </template>
                <template slot="expand" slot-scope="props">
                  <v-container fluid>
                    <v-layout row wrap>
                      <v-flex v-for="i in [0, 1, 2, 3]" :key="i" xs6>
                        <v-list dense>
                          <v-subheader>{{
                            $t("faultsPriority" + i)
                          }}</v-subheader>
                          <v-list-tile
                            v-for="(item, index) in props.item.faults[i].faults"
                            :key="`fault-${index}`"
                          >
                            <v-list-tile-content>
                              <v-list-tile-title>{{
                                item.name
                              }}</v-list-tile-title>
                            </v-list-tile-content>
                          </v-list-tile>
                        </v-list>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <vehicles-create-edit-dialog
      v-if="modal.show"
      v-model="modal.show"
      :mode="modal.mode"
      @saved="fetchItems"
    ></vehicles-create-edit-dialog>

    <delete-dialog
      messageKey="deleteVehicleMessage"
      ref="deleteDialog"
      @confirm="deleteConfirmation"
    ></delete-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  FETCH_VEHICLES,
  FETCH_VEHICLE,
  FETCH_FLEETS,
  DELETE_VEHICLE,
  SHOW_SNACKBAR
} from "@/store/actions.type";
import {
  ROLE_PERMISSION_VEHICLE_MANAGEMENT,
  ROLE_PERMISSION_VEHICLE_VIEW_DETAILS,
  ROLE_PERMISSION_SPARE_PART_VIEW
} from "@/common/permissions";

import refreshButtonMixin from "@/mixins/refreshButton.mixin";
import createButtonMixin from "@/mixins/createButton.mixin";
import searchInputMixin from "@/mixins/searchInput.mixin";
import paginationMixin from "@/mixins/pagination.mixin";
import VehiclesCreateEditDialog from "@/views/Vehicle/VehiclesCreateEditDialog.vue";

export default {
  components: {
    "vehicles-create-edit-dialog": VehiclesCreateEditDialog
  },
  data() {
    return {
      modal: {
        show: false,
        mode: ""
      },
      pagination: {
        rowsPerPage: -1,
        sortBy: "name",
        descending: false
      },
      timer: null
    };
  },
  mixins: [
    refreshButtonMixin,
    createButtonMixin,
    searchInputMixin,
    paginationMixin
  ],
  watch: {
    autoRefreshTime: function() {
      clearInterval(this.timer);
      this.setTimer();
    }
  },
  computed: {
    ...mapGetters([
      "vehicles",
      "vehiclesCount",
      "vehicle",
      "vehicleIsLoading",
      "hasPermission",
      "autoRefreshTime"
    ]),
    hasManagementPermission() {
      return this.hasPermission(ROLE_PERMISSION_VEHICLE_MANAGEMENT);
    },
    hasSparePartsPermission() {
      return this.hasPermission(ROLE_PERMISSION_SPARE_PART_VIEW);
    },
    hasDetailsPermission() {
      return this.hasPermission(ROLE_PERMISSION_VEHICLE_VIEW_DETAILS);
    },
    headers() {
      let headers = [
        { text: this.$t("name"), value: "name" },
        { text: this.$t("odometer"), value: "odometer" },
        { text: this.$t("lastSeen"), value: "last_seen" }
      ];
      if (this.hasDetailsPermission) {
        headers.push(
          { text: this.$t("status"), value: "status" },
          {
            text: this.$t("faultsPriority0"),
            sortable: false,
            fullText: this.$t("faultsPriority1FullText")
          },
          {
            text: this.$t("faultsPriority1"),
            sortable: false,
            fullText: this.$t("faultsPriority1FullText")
          },
          {
            text: this.$t("faultsPriority2"),
            sortable: false,
            fullText: this.$t("faultsPriority2FullText")
          },
          {
            text: this.$t("faultsPriority3"),
            sortable: false,
            fullText: this.$t("faultsPriority3FullText")
          }
        );
      }
      if (this.hasManagementPermission || this.hasSparePartsPermission) {
        headers.push({ text: this.$t("actions"), sortable: false });
      }
      return headers;
    }
  },
  mounted() {
    this.setTimer();
  },
  destroyed() {
    clearInterval(this.timer);
  },
  methods: {
    fetchItems() {
      this.$store.dispatch(FETCH_VEHICLES, [this.pagination, this.search]);
      this.$store.dispatch(FETCH_FLEETS, [this.pagination, this.search]);
    },
    createAction() {
      this.modal.mode = "create";
      this.modal.show = true;
    },
    editAction(id) {
      this.$store.dispatch(FETCH_VEHICLE, id).then(() => {
        this.modal.mode = "edit";
        this.modal.show = true;
      });
    },
    deleteConfirmation(vehicle) {
      this.$store.dispatch(DELETE_VEHICLE, vehicle.id).then(() => {
        this.$store.dispatch(SHOW_SNACKBAR, {
          text: this.$t("deletedSuccessfully", [this.$t("vehicle")]),
          color: "success"
        });

        this.fetchItems();
      });
    },
    getLastSeen(lastSeen) {
      if (lastSeen) {
        return this.$moment(lastSeen).format("LLL");
      }

      return this.$t("never");
    },
    setTimer() {
      if (this.autoRefreshTime > 0) {
        this.timer = setInterval(this.fetchItems, this.autoRefreshTime);
      }
    }
  }
};
</script>

<style scoped></style>
